/**
 * プリセット管理モジュール
 * presets.jsonからプリセット定義を読み込み、型安全な形式に変換
 */
import presetsData from './presets.json';
import { EngraveSettings, TextAlignment, HorizontalAlignment, VerticalAlignment } from '../types';

/**
 * プリセットが適用可能な面のタイプ
 */
export type FaceType = 'front' | 'back';

/**
 * プリセット定義
 */
export interface PresetDefinition {
    /** プリセットの一意識別子 */
    id: string;
    /** プリセットの表示名 */
    name: string;
    /** プリセットの説明 */
    description?: string;
    /** プリセットが適用可能な面 */
    applicableTo: FaceType[];
    /** プリセットの設定値 */
    settings: EngraveSettings;
}

/**
 * 文字列からTextAlignment enumに変換
 */
const parseAlignment = (value: string): TextAlignment => {
    switch (value) {
        case 'Left':
            return TextAlignment.Left;
        case 'Center':
            return TextAlignment.Center;
        case 'Right':
            return TextAlignment.Right;
        default:
            return TextAlignment.Center;
    }
};

/**
 * 文字列からHorizontalAlignment enumに変換
 */
const parseHorizontalAlignment = (value: string): HorizontalAlignment => {
    switch (value) {
        case 'Left':
            return HorizontalAlignment.Left;
        case 'Center':
            return HorizontalAlignment.Center;
        case 'Right':
            return HorizontalAlignment.Right;
        default:
            return HorizontalAlignment.Center;
    }
};

/**
 * 文字列からVerticalAlignment enumに変換
 */
const parseVerticalAlignment = (value: string): VerticalAlignment => {
    switch (value) {
        case 'Top':
            return VerticalAlignment.Top;
        case 'Center':
            return VerticalAlignment.Center;
        case 'Bottom':
            return VerticalAlignment.Bottom;
        default:
            return VerticalAlignment.Center;
    }
};

/**
 * JSONプリセットを型安全なPresetDefinitionに変換
 */
export const PRESETS: PresetDefinition[] = (presetsData.presets || []).map((preset: any) => ({
    id: preset.id,
    name: preset.name,
    description: preset.description,
    applicableTo: preset.applicableTo || ['front', 'back'], // デフォルトは両面で使用可能
    settings: {
        text: preset.settings.text || '',
        fontFamily: preset.settings.fontFamily || 'Noto Sans JP',
        fontSize: preset.settings.fontSize || 12,
        alignment: parseAlignment(preset.settings.alignment),
        isBold: preset.settings.isBold || false,
        isItalic: preset.settings.isItalic || false,
        isVertical: preset.settings.isVertical || false,
        horizontalAlignment: parseHorizontalAlignment(preset.settings.horizontalAlignment),
        verticalAlignment: parseVerticalAlignment(preset.settings.verticalAlignment)
    }
}));

/**
 * IDでプリセットを取得
 * @param id プリセットID
 * @returns プリセット定義（見つからない場合はundefined）
 */
export const getPresetById = (id: string): PresetDefinition | undefined => {
    return PRESETS.find(preset => preset.id === id);
};

/**
 * 指定された面で使用可能なプリセットのみをフィルタリング
 * @param faceType 面のタイプ（'front' または 'back'）
 * @returns フィルタリングされたプリセットの配列
 */
export const getPresetsForFace = (faceType: FaceType): PresetDefinition[] => {
    return PRESETS.filter(preset => preset.applicableTo.includes(faceType));
};
